import { Autocomplete, Box, Pagination, Skeleton, TextField } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from 'store/store';
import {
	findUserByEmail,
	getAllUsersWithPermission,
	givePermission,
	removePermission,
} from 'store/user-service/asyncActions';

import { NamespacesList } from 'core/enums/common';
import useDebounce from 'core/hooks/useDebounce';
import { FoundUser, PaginationType, UserFile } from 'core/types/base';

import { Owner, OwnerInfo } from 'components/Profile/Documents/styled';
import { SharedModal } from 'components/shared/Modal';

import { UserDetails } from './UserDetails';
import * as Styled from './styled';

interface AllowAccessModalProps {
	isOpen: boolean;
	onClose: () => void;
	currentFile: UserFile | null;
}

const Skeletons = () => (
	<>
		{[...Array(4)].map((_e, i) => (
			<Styled.ModalUser key={i}>
				<Owner>
					<Skeleton variant="circular" width={40} height={40} animation="wave" />
					<OwnerInfo sx={{ gap: '5px' }}>
						<Skeleton variant="rounded" width={70} height={10} animation="wave" />
						<Skeleton variant="rounded" width={150} height={10} animation="wave" />
					</OwnerInfo>
				</Owner>
				<Box sx={{ padding: '0 14px' }}>
					<Skeleton variant="rounded" width={125} height={15} animation="wave" />
				</Box>
			</Styled.ModalUser>
		))}
	</>
);

export const AllowAccessModal: FC<AllowAccessModalProps> = ({ isOpen, onClose, currentFile }) => {
	const { t: modalTranslate } = useTranslation(NamespacesList['modals']);

	const dispatch = useAppDispatch();

	const [usersWithAccess, setUsersWithAccess] = useState<FoundUser[]>([]);
	const [usersWithAccessPagination, setUsersWithAccessPagination] = useState<PaginationType>();
	const [gettingUsersWithAccess, setGettingUsersWithAccess] = useState(false);
	const [userSearchQuery, setUserSearchQuery] = useState('');
	const [userSearchResults, setUserSearchResults] = useState<Array<FoundUser>>([]);
	const [selectedUser, setSelectedUser] = useState<FoundUser | null>(null);

	const debouncedUserSearchQuery = useDebounce(userSearchQuery, 400);

	const handleGetUsersWithAccess = async (pageNumber?: number) => {
		if (!pageNumber || pageNumber !== usersWithAccessPagination?.currentPageNumber) {
			setGettingUsersWithAccess(true);
			const { data, ...pagination } = await dispatch(
				getAllUsersWithPermission({
					currentPageNumber: pageNumber || 1,
					recordsPerPage: 4,
					fileName: currentFile?.name || '',
				})
			).unwrap();
			setGettingUsersWithAccess(false);
			setUsersWithAccessPagination(pagination);
			setUsersWithAccess(data);
		}
	};

	const handleUserSearch = async (searchValue: string) => {
		const foundUser = await dispatch(findUserByEmail({ emailPrefix: searchValue })).unwrap();

		setUserSearchResults(foundUser);
	};

	useEffect(() => {
		if (debouncedUserSearchQuery.length >= 1) handleUserSearch(debouncedUserSearchQuery);
		else setUserSearchResults([]);
	}, [debouncedUserSearchQuery]);

	const handleAutocompleteClose = () => {
		if (!selectedUser) setUserSearchResults([]);
	};

	const autoCompleteOptions = useMemo(
		() => userSearchResults.map((user: any) => user.email),
		[userSearchResults]
	);

	const handleSetSelectedUser = (value: string) => {
		setSelectedUser(userSearchResults?.find((user) => user.email === value) || null);
	};

	useEffect(() => {
		if (isOpen) {
			handleGetUsersWithAccess();
		}
	}, [isOpen]);

	const handleCloseModal = () => {
		setSelectedUser(null);
		setUsersWithAccess([]);
		onClose();
	};

	const handleGiveAccess = async (user: FoundUser) => {
		try {
			await dispatch(
				givePermission({ receiverId: user.id, documentName: currentFile?.name || '' })
			).unwrap();
			setSelectedUser(null);
			handleGetUsersWithAccess();
		} catch (error) {
			console.log(error);
		}
	};

	const handleRemoveAccess = async (user: FoundUser) => {
		try {
			await dispatch(
				removePermission({ receiverId: user.id, documentName: currentFile?.name || '' })
			).unwrap();
			handleGetUsersWithAccess();
		} catch (error) {
			console.log(error);
		}
	};

	const getPageNumber = useMemo(() => {
		if (usersWithAccessPagination) {
			return Math.ceil(
				usersWithAccessPagination?.totalRecordsFound / usersWithAccessPagination?.recordsPerPage
			);
		}

		return 1;
	}, [usersWithAccessPagination]);

	const currentPageNumber = useMemo(
		() => usersWithAccessPagination?.currentPageNumber || 1,
		[usersWithAccessPagination]
	);

	return (
		<SharedModal isOpen={isOpen} handleClose={handleCloseModal}>
			<Styled.ModalContent
				className="not-rtl-able"
				sx={{
					width: { xs: '100%', sm: '438px' },
				}}
			>
				<Styled.ModalTitle>{modalTranslate('allowAccessModal.title')}</Styled.ModalTitle>
				<Styled.ModalMainContent className="rtl-able">
					<Autocomplete
						disablePortal
						onClose={handleAutocompleteClose}
						onChange={(_, value) => handleSetSelectedUser(value as string)}
						options={autoCompleteOptions}
						sx={{ width: '100%' }}
						renderInput={(params) => (
							<TextField
								{...params}
								label={modalTranslate('allowAccessModal.addUsers')}
								onChange={(event) => setUserSearchQuery(event.target.value)}
							/>
						)}
					/>
					<Styled.ModalUsers>
						<Styled.ModalSubtitle>
							{modalTranslate('allowAccessModal.usersWithAccess')}
						</Styled.ModalSubtitle>
						{!selectedUser ? (
							<>
								<UserDetails
									key={`${currentFile?.firstName}${currentFile?.lastName}`}
									user={currentFile}
									removeAccess={handleRemoveAccess}
									haveSuccess={true}
								/>
								{gettingUsersWithAccess ? (
									<Skeletons />
								) : (
									usersWithAccess.map((user: any) => (
										<UserDetails
											key={`${user.firstName}${user.lastName}`}
											user={user}
											removeAccess={handleRemoveAccess}
											haveSuccess={true}
										/>
									))
								)}
							</>
						) : (
							<UserDetails
								key={`${selectedUser.firstName}${selectedUser.lastName}`}
								user={selectedUser}
								removeAccess={handleRemoveAccess}
								giveAccess={handleGiveAccess}
								haveSuccess={usersWithAccess.some((user: any) => user.id === selectedUser.id)}
								currentFile={currentFile}
							/>
						)}
					</Styled.ModalUsers>
					<Pagination
						count={getPageNumber}
						page={currentPageNumber}
						onChange={(_, pageNumber) => handleGetUsersWithAccess(pageNumber)}
						variant="outlined"
						shape="rounded"
						sx={{ marginTop: 'auto' }}
					/>
				</Styled.ModalMainContent>
			</Styled.ModalContent>
		</SharedModal>
	);
};
